import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  useWebsiteContactRequests,
  useUpdateWebsiteContactRequestReadStatus,
  type WebsiteContactRequest,
} from '../hooks/useWebsiteContactRequests';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const MessageCell = styled.td`
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(700px, 100%);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 0.8rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const StatusPill = styled.span<{ $isRead: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $isRead }) => ($isRead ? 'var(--surfaceVariant)' : 'var(--primary)')};
  color: ${({ $isRead }) => ($isRead ? 'var(--onSurfaceVariant)' : 'var(--onPrimary)')};
`;

const StatusButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 9999px;
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const formatVisitDate = (value?: string | null) => {
  if (!value) return '—';

  const parsed = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
};

export const ContactRequestsPage: React.FC = () => {
  const { data, isLoading, error } = useWebsiteContactRequests();
  const [selectedRequest, setSelectedRequest] = useState<WebsiteContactRequest | null>(null);
  const updateReadStatus = useUpdateWebsiteContactRequestReadStatus();
  const hasData = (data?.length ?? 0) > 0;
  const openRequest = (request: WebsiteContactRequest) => {
    setSelectedRequest(request);
    if (!request.is_read) {
      updateReadStatus.mutate({ id: request.id, isRead: true });
      setSelectedRequest((prev) =>
        prev?.id === request.id
          ? { ...prev, is_read: true, read_at: new Date().toISOString() }
          : prev
      );
    }
  };

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedRequest(null);
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  return (
    <Page>
      <HeaderTitle>Contact Requests</HeaderTitle>
      <HelperText>
        Messages from the public website contact, visit, and messaging-alert signup forms.
      </HelperText>
      {isLoading && <HelperText>Loading…</HelperText>}
      {error && (
        <HelperText style={{ color: 'var(--error)' }}>
          Could not load contact requests. You may need to run the migration for website_contact_requests.
        </HelperText>
      )}

      {!isLoading && hasData && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Visit Date</th>
                <th>Message</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((request) => (
                <ClickableRow
                  key={request.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRequest(request)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openRequest(request);
                    }
                  }}
                >
                  <td>{request.name ?? '—'}</td>
                  <td>{request.email ?? '—'}</td>
                  <td>{request.subject ?? '—'}</td>
                  <td>{formatVisitDate(request.visit_date)}</td>
                  <MessageCell title={request.message ?? undefined}>
                    {request.message ?? '—'}
                  </MessageCell>
                  <td>
                    <StatusPill $isRead={request.is_read}>{request.is_read ? 'Read' : 'Unread'}</StatusPill>
                  </td>
                  <td>
                    {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                  </td>
                </ClickableRow>
              ))}
            </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}

      {!isLoading && !hasData && !error && (
        <HelperText>No contact requests yet.</HelperText>
      )}

      {selectedRequest && (
        <ModalOverlay onClick={() => setSelectedRequest(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Contact Request Details</ModalTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StatusButton
                  type="button"
                  onClick={async () => {
                    const nextIsRead = !selectedRequest.is_read;
                    await updateReadStatus.mutateAsync({ id: selectedRequest.id, isRead: nextIsRead });
                    setSelectedRequest((prev) =>
                      prev ? { ...prev, is_read: nextIsRead, read_at: nextIsRead ? new Date().toISOString() : null } : prev
                    );
                  }}
                  disabled={updateReadStatus.isPending}
                >
                  {selectedRequest.is_read ? 'Mark as unread' : 'Mark as read'}
                </StatusButton>
                <ModalCloseButton
                  type="button"
                  aria-label="Close contact request details"
                  onClick={() => setSelectedRequest(null)}
                >
                  ×
                </ModalCloseButton>
              </div>
            </ModalHeader>
            <ModalBody>
              <DetailGroup>
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{selectedRequest.name ?? '—'}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{selectedRequest.email ?? '—'}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Subject</DetailLabel>
                <DetailValue>{selectedRequest.subject ?? '—'}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Message</DetailLabel>
                <DetailValue>{selectedRequest.message ?? '—'}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Visit date</DetailLabel>
                <DetailValue>{formatVisitDate(selectedRequest.visit_date)}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Submitted</DetailLabel>
                <DetailValue>
                  {selectedRequest.created_at
                    ? new Date(selectedRequest.created_at).toLocaleString()
                    : '—'}
                </DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>{selectedRequest.is_read ? 'Read' : 'Unread'}</DetailValue>
              </DetailGroup>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};
